import { Metric } from "web-vitals";
import { CompassError } from "./errorTypes";

export enum CommonDataRequestType {
  DISTINCT_FUNDS = "distinct_funds",
  DISTINCT_FUNDS_W_MANAGER = "distinct_funds_v2",
  MY_FUNDS_METRICS = "my_funds_metrics",
  HOLDING_COMPANIES_METRICS = "holding_companies_metrics",
  EXPOSURE = "exposure",
}

export interface DistinctFundsRequest {
  /**
   * Represents a request for distinct funds.
   *
   * Attributes:
   * distinct_funds: A placeholder attribute for distinct funds.
   */
  distinct_funds: null;
}

export interface MyFundsMetricsRequest {
  /**
   * Represents a request for my funds metrics.
   *
   * Attributes:
   * my_funds_metrics: A placeholder attribute for my funds metrics.
   */
  my_funds_metrics: null;
}

export interface ExposureRequest {
  compass_cid: string;
}

// Conditional type that constrains T to be either DistinctFundsRequest or MyFundsMetricsRequest
export type CommonDataItem<T> = T extends
  | DistinctFundsRequest
  | MyFundsMetricsRequest
  | ExposureRequest
  ? {
      /**
       * Represents a common data item.
       *
       * Attributes:
       * name: The name of the common data item.
       * item: The item associated with the common data item.
       */
      name: CommonDataRequestType;
      item: T;
    }
  : never;

export interface FundNamesItem {
  portfolio_index_id: string;
  is_index: boolean;
}

export interface FundsWManagerItem {
  fund_id: string;
  fund_name: string;
  is_index: boolean;
  manager_id: string;
  latest_date: string; // ISO 8601 date string
}

export type ManagerItem = {
  manager_id: string;
  manager_name: string;
  email: string;
  image: string;
};

export interface CommonDataResponse<
  T extends FundNamesItem[] | Metric[] | ManagerItem[]
> {
  /**
   * Represents a common data response.
   *
   * Attributes:
   * status: The status of the common data response.
   * message: The message associated with the common data response.
   * data: The data associated with the common data response, which could be an array of FundNamesItem or MyFundsMetricsItem.
   */
  status: string;
  message: string;
  data?: T;
  error?: CompassError;
}
